import React, { useState } from 'react'
import { Form, Button, Table } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { client } from '../../utils'
import { MEModal, itemIdxDisplayer } from '@CommonComponents'
import { Pagination } from '@CommonComponents/buttons'

const RESULTS_PER_PAGE = 10
// a course can carry ~8 authors; showing them all turns every row into a wall of text
const MAX_VISIBLE_ROYALTIES = 2

const formatRate = rate =>
  rate === null || rate === undefined ? '--' : `${(rate * 100).toFixed(2)}%`

const RoyaltyList = ({ royalties }) => {
  const [expanded, setExpanded] = useState(false)
  if (royalties.length === 0) {
    return <em>None</em>
  }
  const visible = expanded
    ? royalties
    : royalties.slice(0, MAX_VISIBLE_ROYALTIES)
  const hiddenCount = royalties.length - visible.length
  return (
    <div>
      {visible.map((r, key) => (
        <div key={`rl${key}`} className="text-nowrap">
          {r.name || <em>No account</em>}{' '}
          <span className="text-muted">({formatRate(r.rate)})</span>
        </div>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="btn btn-link btn-xs-myed p-0"
          onClick={() => setExpanded(true)}
        >
          +{hiddenCount} more
        </button>
      )}
      {expanded && royalties.length > MAX_VISIBLE_ROYALTIES && (
        <button
          type="button"
          className="btn btn-link btn-xs-myed p-0"
          onClick={() => setExpanded(false)}
        >
          Show fewer
        </button>
      )}
    </div>
  )
}

RoyaltyList.propTypes = {
  royalties: PropTypes.array,
}

export default class App extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      currentRoyalties: this.props.royalties.currentRoyalties,
      availableAuthors: this.props.royalties.availableAuthors,
      newAuthor: '',
      newAuthorRate: 0.0,
      // push-to-children
      showPushModal: false,
      pushPreview: null,
      pushLoading: false,
      pushApplying: false,
      pushError: null,
      pushResult: null,
      confirmingEmptyPush: false,
      pushPageNum: 1,
      resultPageNum: 1,
    }
    this.handleRateChange = this.handleRateChange.bind(this)
    this.handleAuthorChange = this.handleAuthorChange.bind(this)
    this.addAuthor = this.addAuthor.bind(this)
    this.removeAuthor = this.removeAuthor.bind(this)
    this.openPushModal = this.openPushModal.bind(this)
    this.closePushModal = this.closePushModal.bind(this)
    this.applyPush = this.applyPush.bind(this)
    this.loadPushPage = this.loadPushPage.bind(this)
    this.changePushPage = this.changePushPage.bind(this)
    // Bumped on every preview request (and on open/close) so a response that lands after
    // the modal was closed and reopened -- or after a later page was requested -- is
    // discarded instead of overwriting fresher state.  An instance field rather than
    // state, because loadPushPage has to read the current value synchronously.
    this.previewRequestSeq = 0
  }

  async removeAuthor(accountID) {
    let coursenum = this.props.coursenum
    let url = `/instructor/admin_setup.remove_stripe_connect_author/${coursenum}/${accountID}/`
    let response = await client.post(url)
    this.setState({
      currentRoyalties: response.data.currentRoyalties,
    })
  }

  async addAuthor(e) {
    e.preventDefault()
    let coursenum = this.props.coursenum
    let rate = this.state.newAuthorRate * 100
    let url = `/instructor/admin_setup.add_stripe_connect_author/${coursenum}/${this.state.newAuthor}/${rate}/`
    let response = await client.post(url)
    this.setState({
      newAuthor: '',
      newAuthorRate: 0.0,
      currentRoyalties: response.data.currentRoyalties,
    })
  }

  handleRateChange(e) {
    this.setState({
      newAuthorRate: e.target.value,
    })
  }

  handleAuthorChange(e) {
    this.setState({
      newAuthor: e.target.value,
    })
  }

  openPushModal(e) {
    // the button holds :focus after the modal closes, which leaves it looking pressed
    if (e && e.currentTarget) {
      e.currentTarget.blur()
    }
    this.setState({
      showPushModal: true,
      pushPreview: null,
      pushError: null,
      pushResult: null,
      confirmingEmptyPush: false,
      pushPageNum: 1,
    })
    this.previewRequestSeq += 1
    this.loadPushPage({ pageNum: 1 })
  }

  // Resolves true once the preview loads, false when this request fails, and
  // undefined when a later request replaces it.
  async loadPushPage({ pageNum }) {
    const requestSeq = ++this.previewRequestSeq
    this.setState({ pushLoading: true, pushPageNum: pageNum })
    let url = `/instructor/admin_setup.preview_royalty_push/${this.props.coursenum}/`
    let body = new URLSearchParams()
    body.append('page_num', pageNum)
    try {
      let response = await client.post(url, body)
      if (requestSeq !== this.previewRequestSeq) {
        return // a newer preview request has since been started
      }
      this.setState({ pushPreview: response.data, pushLoading: false })
      return true
    } catch (error) {
      if (requestSeq !== this.previewRequestSeq) {
        return
      }
      this.setState({
        pushLoading: false,
        pushError:
          (error.response &&
            error.response.data &&
            error.response.data.error) ||
          'Could not load the preview.',
      })
      return false
    }
  }

  // The pager counts a rejection as a failed page load.
  changePushPage(pageNum) {
    return this.loadPushPage({ pageNum }).then(ok => {
      if (ok === false) throw new Error('The preview page did not load.')
    })
  }

  closePushModal() {
    this.previewRequestSeq += 1 // drop any preview still in flight
    this.setState({ showPushModal: false, confirmingEmptyPush: false })
  }

  async applyPush() {
    let isEmptyPush =
      this.state.pushPreview && this.state.pushPreview.isEmptyPush
    // An empty push wipes every child's royalties, so make the user say so twice.
    if (isEmptyPush && !this.state.confirmingEmptyPush) {
      this.setState({ confirmingEmptyPush: true })
      return
    }

    this.setState({ pushApplying: true, pushError: null })
    let url = `/instructor/admin_setup.push_royalties_to_children/${this.props.coursenum}/`
    let body = new URLSearchParams()
    if (isEmptyPush) {
      body.append('confirm_empty', 'true')
    }
    try {
      let response = await client.post(url, body)
      this.setState({
        pushApplying: false,
        pushResult: response.data,
        pushPreview: null,
        confirmingEmptyPush: false,
        resultPageNum: 1,
      })
    } catch (error) {
      this.setState({
        pushApplying: false,
        confirmingEmptyPush: false,
        pushError:
          (error.response &&
            error.response.data &&
            error.response.data.error) ||
          'The push failed. No courses were changed.',
      })
    }
  }

  renderProgressBar() {
    // The push is a single request, so there is no real percentage to report -- this is
    // an indeterminate bar that simply shows the work is in flight.
    return (
      <div className="progress mb-3">
        <div
          className="progress-bar progress-bar-striped active"
          role="progressbar"
          aria-valuenow="100"
          aria-valuemin="0"
          aria-valuemax="100"
          style={{ width: '100%' }}
        >
          Pushing royalties&hellip;
        </div>
      </div>
    )
  }

  renderPushModalBody() {
    const {
      pushLoading,
      pushPreview,
      pushError,
      pushResult,
      pushApplying,
      confirmingEmptyPush,
      resultPageNum,
    } = this.state

    // A page change keeps the loaded preview's summary and pager on screen.
    if (pushLoading && !pushPreview) {
      return <p className="my-4">Loading preview&hellip;</p>
    }

    if (pushResult) {
      const perPage = RESULTS_PER_PAGE
      const start = (resultPageNum - 1) * perPage
      const page = pushResult.results.slice(start, start + perPage)
      return (
        <div>
          <div className="alert alert-success mb-4">
            Updated {pushResult.coursesUpdated} course
            {pushResult.coursesUpdated === 1 ? '' : 's'}: removed{' '}
            {pushResult.rowsRemoved} royalt
            {pushResult.rowsRemoved === 1 ? 'y' : 'ies'} and added{' '}
            {pushResult.rowsCreated}.
          </div>
          {pushResult.results.length > perPage && (
            <p className="text-muted mb-1">
              Showing
              {itemIdxDisplayer(
                perPage,
                resultPageNum,
                page.length,
                pushResult.results.length
              )}
            </p>
          )}
          <Table striped bordered className="table-sm mb-3">
            <thead>
              <tr>
                <th>Course</th>
                <th>Removed</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {page.map((r, key) => (
                <tr key={`res${key}`}>
                  <td>
                    {r.coursenum} &mdash; {r.title}
                  </td>
                  <td>{r.removed}</td>
                  <td>{r.added}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {pushResult.results.length > perPage && (
            <div className="mb-4">
              <Pagination
                currentPage={resultPageNum}
                rate={perPage}
                total={pushResult.results.length}
                setCurrentPage={pageNum =>
                  this.setState({ resultPageNum: pageNum })
                }
              />
            </div>
          )}
          <div className="mt-4">
            <Button
              className="btn btn-myed btn-default btn-default-myed"
              onClick={this.closePushModal}
            >
              Close
            </Button>
          </div>
        </div>
      )
    }

    if (pushError && !pushPreview) {
      return (
        <div>
          <div className="alert alert-danger mb-4">{pushError}</div>
          <div className="mt-4">
            <Button
              className="btn btn-myed btn-default btn-default-myed"
              onClick={this.closePushModal}
            >
              Close
            </Button>
          </div>
        </div>
      )
    }

    if (!pushPreview) {
      return null
    }

    return (
      <div>
        {pushError && (
          <div className="alert alert-danger mb-4">{pushError}</div>
        )}
        {pushPreview.warnings.map((w, key) => (
          <div className="alert alert-warning mb-4" key={`w${key}`}>
            {w}
          </div>
        ))}

        {pushPreview.isEmptyPush ? (
          <div className="alert alert-danger mb-4">
            <strong>This master has no royalties.</strong> Pushing will remove
            all royalties from {pushPreview.childCount} course
            {pushPreview.childCount === 1 ? '' : 's'} and leave them with none.
          </div>
        ) : (
          <div className="mb-4">
            <p className="mb-1">
              These royalties will replace whatever each child course currently
              has:
            </p>
            <Table striped bordered className="table-sm">
              <thead>
                <tr>
                  <th>Author</th>
                  <th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {pushPreview.masterRoyalties.map((r, key) => (
                  <tr key={`m${key}`}>
                    <td>{r.name || <em>No account</em>}</td>
                    <td>{formatRate(r.rate)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <p className="mb-1">
          <strong>
            {pushPreview.childCount} course
            {pushPreview.childCount === 1 ? '' : 's'} will be updated
            {pushPreview.changingCount === pushPreview.childCount
              ? '.'
              : `, ${pushPreview.changingCount} of which will actually change.`}
          </strong>
          {pushPreview.childCount > pushPreview.perPage && (
            <span className="text-muted">
              {' '}
              Showing
              {itemIdxDisplayer(
                pushPreview.perPage,
                pushPreview.pageNum,
                pushPreview.children.length,
                pushPreview.childCount
              )}
              &mdash; the push applies to all of them.
            </span>
          )}
        </p>
        {pushLoading ? (
          <p className="my-4">Loading preview&hellip;</p>
        ) : (
          <Table striped bordered className="table-sm mb-3">
            <thead>
              <tr>
                <th>Course</th>
                <th>Status</th>
                <th>Effect</th>
                <th>Current Royalties</th>
              </tr>
            </thead>
            <tbody>
              {pushPreview.children.map((c, key) => (
                <tr key={`c${key}`}>
                  <td>
                    {c.coursenum} &mdash; {c.title}
                  </td>
                  <td>{c.status}</td>
                  <td>
                    {c.matchesMaster ? (
                      <span className="label label-default">
                        Matches master
                      </span>
                    ) : (
                      <span className="label label-primary">Will change</span>
                    )}
                  </td>
                  <td>
                    <RoyaltyList royalties={c.currentRoyalties} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {pushPreview.childCount > pushPreview.perPage && (
          <div className="mb-4">
            <Pagination
              currentPage={pushPreview.pageNum}
              rate={pushPreview.perPage}
              total={pushPreview.childCount}
              setCurrentPage={this.changePushPage}
            />
          </div>
        )}

        {!pushLoading && confirmingEmptyPush && (
          <div className="alert alert-danger mb-4">
            <strong>Last chance.</strong> You are about to wipe out the
            royalties on {pushPreview.childCount} course
            {pushPreview.childCount === 1 ? '' : 's'}. They will earn nothing on
            future sales until royalties are set again. Are you sure?
          </div>
        )}

        {pushApplying && this.renderProgressBar()}

        {!pushLoading && (
          <div className="mt-4">
            <Button
              variant={pushPreview.isEmptyPush ? 'danger' : 'primary'}
              className={
                pushPreview.isEmptyPush
                  ? 'btn btn-myed btn-danger btn-danger-myed mr-2'
                  : 'btn btn-myed btn-primary btn-primary-myed mr-2'
              }
              disabled={pushApplying || pushPreview.childCount === 0}
              onClick={this.applyPush}
            >
              {pushApplying
                ? 'Pushing…'
                : confirmingEmptyPush
                ? 'Yes, wipe them out'
                : pushPreview.isEmptyPush
                ? 'Remove All Royalties'
                : 'Push to Child Courses'}
            </Button>
            <Button
              className="btn btn-myed btn-default btn-default-myed"
              disabled={pushApplying}
              onClick={this.closePushModal}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    )
  }

  render() {
    return (
      <div>
        <h3>Stripe Connect Royalties</h3>
        <Table striped bordered className="table-sm" hover>
          <thead>
            <tr>
              <th>Author</th>
              <th>Rate</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {this.state.currentRoyalties.map((x, key) => (
              <tr key={`r${key}`}>
                <td>{x.name}</td>
                <td>{(x.rate * 100).toFixed(2)}%</td>
                <td>
                  <Button
                    variant="danger"
                    className="btn btn-myed btn-danger btn-danger-myed btn-xs-myed"
                    onClick={() => this.removeAuthor(x.account_id)}
                  >
                    <i className="fa-regular fa-times mr-1" /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {this.state.currentRoyalties.map((royalty, i) => {
          if (!royalty.account_id) {
            return (
              <p key={i} className="text-danger">
                {royalty.name} has no Stripe Account ID
              </p>
            )
          }
        })}
        <h3>Add New Author</h3>
        <Form className="form-inline-b3">
          <Form.Group controlId="formControlsSelect" className="mr-2">
            <Form.Control
              as="select"
              placeholder="select"
              onChange={this.handleAuthorChange}
              style={{ width: '125px' }}
            >
              <option value="">Select Author</option>
              {this.state.availableAuthors.map((x, key) => (
                <option key={key} value={x.accountID}>
                  {x.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group className="mr-2">
            <label htmlFor="rate" className="hidden_label">
              Rate
            </label>
            <Form.Control
              id="rate"
              type="text"
              placeholder="Rate (decimal)"
              onChange={this.handleRateChange}
              style={{ width: '125px' }}
            />
          </Form.Group>
          <Button
            type="submit"
            variant="success"
            className="btn-myed btn-success-myed btn-sm-myed"
            disabled={!this.state.newAuthor || !this.state.newAuthorRate}
            onClick={this.addAuthor}
          >
            <i className="fa-light fa-plus mr-1" /> Add
          </Button>
        </Form>

        {this.props.isMaster && (
          <div className="mt-4">
            <h3>Push to Child Courses</h3>
            <p>
              Replace the royalties on every child course of this master with
              the ones above. Each child&rsquo;s existing royalties are removed
              first.
            </p>
            <Button
              className="btn btn-myed btn-default btn-default-myed btn-sm-myed"
              disabled={this.props.childCount === 0}
              onClick={this.openPushModal}
            >
              <i className="fa-light fa-arrow-down-to-line mr-1" />
              {this.props.childCount === 0
                ? 'No Child Courses'
                : `Push to ${this.props.childCount} Child Course${
                    this.props.childCount === 1 ? '' : 's'
                  }`}
            </Button>
          </div>
        )}

        <MEModal
          show={this.state.showPushModal}
          close={this.closePushModal}
          size="lg"
          title="Push Royalties to Child Courses"
        >
          {this.renderPushModalBody()}
        </MEModal>
      </div>
    )
  }
}

App.propTypes = {
  coursenum: PropTypes.string,
  royalties: PropTypes.object,
  isMaster: PropTypes.bool,
  childCount: PropTypes.number,
}
